import {
  SAVED_FILTERS_COLLECTIONS,
  type SavedFiltersCollection,
  type SavedFiltersScope,
} from './types';

/**
 * Every rule about where a Saved Filter lives in localStorage. The key carries its owner — the
 * internal GreyScout user id (`gsUid`) — ahead of the collection, so two people on one origin never
 * share a bucket. Once the session adopts this builder, no other module may build, parse or assume
 * a Saved Filters key.
 */

/** The internal GreyScout user id (`gsUid`) a Saved Filter belongs to. */
export type SavedFilterOwner = string;

const STORAGE_PREFIX = 'gsSavedFilters';

/** Retired ownerless key, never read and with no owner-scoped target — migration only deletes it. */
const ORPHANED_STORAGE_KEY = `${STORAGE_PREFIX}:stores`;

const buildScopeSuffix = (scope: SavedFiltersScope) => {
  if (scope.kind === 'global') return 'global';

  return scope.companyId ? `scoped:${scope.companyId}` : 'scoped';
};

export const buildSavedFiltersStorageKey = ({
  owner,
  collection,
  scope,
}: {
  owner: SavedFilterOwner;
  collection: SavedFiltersCollection;
  scope: SavedFiltersScope;
}) => `${STORAGE_PREFIX}:${owner}:${collection}:${buildScopeSuffix(scope)}`;

const isSavedFiltersCollection = (segment?: string): segment is SavedFiltersCollection => (
  SAVED_FILTERS_COLLECTIONS.includes(segment as SavedFiltersCollection)
);

/**
 * A legacy key names a collection where an owner-scoped key names its owner. `gsUid` is numeric, so
 * a collection can never sit in the owner position and an owned key is never read as a legacy one.
 */
const isLegacyStorageKey = (key: string) => {
  if (key === ORPHANED_STORAGE_KEY) return true;

  const [prefix, collection, scopeKind, ...rest] = key.split(':');
  if (prefix !== STORAGE_PREFIX || !isSavedFiltersCollection(collection)) return false;

  return scopeKind === 'global'
    ? rest.length === 0
    : scopeKind === 'scoped' && rest.length <= 1;
};

export const listLegacySavedFiltersStorageKeys = (): string[] => {
  const storage = globalThis.localStorage;

  return Array.from({ length: storage.length }, (_, index) => storage.key(index))
    .filter((key): key is string => key !== null && isLegacyStorageKey(key));
};

/**
 * Moves one legacy key under its owner. This is a storage-level move: the stored value is copied
 * verbatim, so a later persisted-state version bump keeps working. An existing target wins; the
 * legacy key goes either way, which bounds the "first user after deploy inherits it" residual to
 * one occurrence.
 */
export const migrateLegacySavedFiltersStorageKey = ({
  legacyKey,
  owner,
}: {
  legacyKey: string;
  owner: SavedFilterOwner;
}) => {
  if (!isLegacyStorageKey(legacyKey)) return;

  const storage = globalThis.localStorage;
  const value = storage.getItem(legacyKey);
  storage.removeItem(legacyKey);

  if (legacyKey === ORPHANED_STORAGE_KEY || value === null) return;

  const ownedKey = legacyKey.replace(`${STORAGE_PREFIX}:`, `${STORAGE_PREFIX}:${owner}:`);
  if (storage.getItem(ownedKey) !== null) return;

  storage.setItem(ownedKey, value);
};

/** The whole ownerless estate, moved under one owner: the pass callers actually want. */
export const migrateLegacySavedFilters = (owner: SavedFilterOwner) => {
  listLegacySavedFiltersStorageKeys().forEach((legacyKey) => (
    migrateLegacySavedFiltersStorageKey({ legacyKey, owner })
  ));
};
